<template>
    <div>
        <b-card no-body header-class="d-flex justify-content-between align-items-start">
            <template v-slot:header>
                <h5>Filter</h5>
                <b-button size="sm" variant="outline-secondary" :disabled="courtRegistryIds.length == 0 && interviewOrganisationIds.length == 0" v-on:click="clearFilters">Clear</b-button>
            </template>
            <div class="card-body">
                <b-form-row v-if="allCourtRegistryOptions && allCourtRegistryOptions.length">
                    <b-form-group label="Court/Tribunal/Other" label-for="courtRegistryIds" class="col-md-12">
                        <select2 name="courtRegistryIds"
                                 v-model="courtRegistryIds"
                                 :options="allCourtRegistryOptions"
                                 multiple
                                 in-b-modal>
                        </select2>
                    </b-form-group>
                </b-form-row>
                <b-form-row v-if="allInterviewOrganisationOptions && allInterviewOrganisationOptions.length">
                    <b-form-group label="Interview Organisations" label-for="interviewOrganisationIds" class="col-md-12">
                        <select2 name="interviewOrganisationIds"
                                 v-model="interviewOrganisationIds"
                                 :options="allInterviewOrganisationOptions"
                                 multiple
                                 in-b-modal>
                        </select2>
                    </b-form-group>
                </b-form-row>
            </div>
        </b-card>

        <div v-if="canCreatePublicHolidays"><i>Click on any dates to enter a new public holiday</i></div>
        <div v-if="canModifyPublicHolidays"><i>Click entry to edit / delete</i></div>

        <ejs-schedule ref="schedule"
                        :firstDayOfWeek="1"
                        :selectedDate="selectedDate"
                        :eventSettings="eventSettings"
                        :currentView="currentView"
                        :popupOpen="onPopupOpen"
                        :eventRendered="onEventRendered"
                        :rowAutoHeight="rowAutoHeight">
            <e-views>
                <e-view option="Month"></e-view>
            </e-views>
            <template v-slot:tooltipTemplate="{ data }">
                <div class="tooltip-wrap e-schedule-event-tooltip">
                    <div class="content-area">
                        <div class="eventname">{{data.subject}}</div>
                        <div class="eventdate">{{getEventDateString(data)}} </div>
                        <div v-if="data.recurring"><i class="fas fa-sync-alt whiteiconcolor"></i>&nbsp;Recurring</div>
                    </div>
                </div>
            </template>
        </ejs-schedule>

        <validation-observer v-slot="vo" slim>
            <b-modal size="lg"
                     centered
                     :title="createOrEditTitle"
                     v-model="createEventDialogShown"
                     v-on:ok.prevent="vo.handleSubmit(createOrEditEvent)">
                <b-form-row>
                    <validation-provider rules="required" name="Description" v-slot="vp" slim>
                        <b-form-group label="Description" label-for="currentEvent.description" class="col-md-12">
                            <b-form-input name="description" v-model="currentEvent.description" />
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
                <b-form-row>
                    <validation-provider ref="startDateValidationProvider"
                                         rules="required"
                                         name="Start Date"
                                         v-slot="{ valid, errors }"
                                         slim>
                        <b-form-group label="Start Date"
                                      label-for="startDate"
                                      class="col-md-4">
                            <ejs-datepicker name="startDate"
                                            v-model="currentEvent.startDate"
                                            format="d/M/yyyy"
                                            @open="resetDateValidationProviders()">
                            </ejs-datepicker>
                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider ref="endDateValidationProvider"
                                         name="End Date"
                                         v-slot="{ valid, errors }"
                                         slim>
                        <b-form-group label="End Date"
                                      label-for="endDate"
                                      class="col-md-4">
                            <ejs-datepicker name="endDate"
                                            v-model="currentEvent.endDate"
                                            format="d/M/yyyy"
                                            @open="resetDateValidationProviders()">
                            </ejs-datepicker>
                            <b-form-invalid-feedback :state="errors[0] ? false : null">{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
                <b-form-row>
                    <b-form-group label="Recurrs Annually" label-for="currentEvent.recurring" class="col-md-3">
                        <b-form-checkbox name="recurring" v-model="currentEvent.recurring" class="mb-2" switch>
                        </b-form-checkbox>
                    </b-form-group>
                </b-form-row>
                <b-form-row v-if="allCourtRegistryOptions && allCourtRegistryOptions.length">
                    <b-form-group label="Court/Tribunal/Other" label-for="currentEvent.applicableAtCourtRegistryIds" class="col-md-12">
                        <select2 name="applicableAtCourtRegistryIds"
                                 v-model="currentEvent.applicableAtCourtRegistryIds"
                                 :options="allCourtRegistryOptions"
                                 multiple
                                 in-b-modal>
                        </select2>
                    </b-form-group>
                </b-form-row>
                <b-form-row v-if="allInterviewOrganisationOptions && allInterviewOrganisationOptions.length">
                    <b-form-group label="Interview Organisations" label-for="currentEvent.applicableAtInterviewOrganisationIds" class="col-md-12">
                        <select2 name="applicableAtInterviewOrganisationIds"
                                 v-model="currentEvent.applicableAtInterviewOrganisationIds"
                                 :options="allInterviewOrganisationOptions"
                                 multiple
                                 in-b-modal>
                        </select2>
                    </b-form-group>
                </b-form-row>
                <template #modal-footer>
                    <b-button v-on:click="confirmDeleteEvent" v-if="currentEvent.publicHolidayId && canDeletePublicHolidays" variant="danger">Delete Event</b-button>
                    <b-button v-on:click="createEventDialogShown = false" variant="secondary">Cancel</b-button>
                    <b-button v-on:click="vo.handleSubmit(createOrEditEvent)" variant="primary">Save</b-button>
                </template>

                <b-form-row>
                    <entity-history-list name="entityHistory"
                                         entity-type-name="PublicHoliday"
                                         :entity-id="currentEvent.publicHolidayId">
                    </entity-history-list>
                </b-form-row>
            </b-modal>
            </validation-observer>
    </div>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import { DataManager } from '@syncfusion/ej2-data';
    import { CustomDataAdaptor } from '@syncfusion/ej2-data';
    //import { UrlAdaptor } from '@syncfusion/ej2-data';
    import { SchedulePlugin, Month } from '@syncfusion/ej2-vue-schedule';
    import DateTimeMixin from '../mixins/DateTimeMixin';
    import ErrorsMixin from '../mixins/ErrorsMixin';

    //import { DropDownList } from '@syncfusion/ej2-dropdowns';
    //import { createElement } from '@syncfusion/ej2-base';

    import moment from 'moment';
    import 'moment/locale/en-au';
    moment.locale('en-au');

    import axios from 'axios';

    Vue.use(BootstrapVue);
    Vue.use(SchedulePlugin);

    //class CustomAdaptor extends UrlAdaptor { }

    export default {
        name: 'PublicHolidays',
        mixins: [DateTimeMixin, ErrorsMixin],
        provide: {
            schedule: [Month]
        },
        props: {
            canCreatePublicHolidays: {
                type: Boolean,
                default: false
            },
            canModifyPublicHolidays: {
                type: Boolean,
                default: false
            },
            canDeletePublicHolidays: {
                type: Boolean,
                default: false
            },
            allCourtRegistryOptions: {
                type: Array,
                default: null
            },
            allInterviewOrganisationOptions: {
                type: Array,
                default: null
            },
        },
        data: function () {
            return {
                schedulerInstance: null,
                eventSettings: {
                    dataSource: new DataManager({
                        url: '/api/publicholiday/schedule',
                        //adaptor: new CustomAdaptor(),
                        adaptor: new CustomDataAdaptor({
                            getData: this.getData,
                        })
                    }),
                    fields: {
                        id: { name: 'id' },
                        subject: { name: 'subject', title: 'Description' },
                        startTime: { name: 'startTime', title: 'Start Date' },
                        endTime: { name: 'endTime', title: 'End Date' },
                        isAllDay: { name: 'isAllDay' },
                        recurring: { name: 'recurring' },
                        //applicableAtCourtRegistryIds: { name: 'applicableAtCourtRegistryIds' },
                        //applicableAtInterviewOrganisationIds: { name: 'applicableAtInterviewOrganisationIds' },
                    },
                    enableTooltip: true,
                    tooltipTemplate: 'tooltipTemplate'
                },
                selectedDate: new Date(),
                rowAutoHeight: true,
                currentView: 'Month',
                enableTooltip: true,
                createEventDialogShown: false,
                createOrEditTitle: 'Create Public Holiday',

                currentEvent: {
                    publicHolidayId: null,
                    startDate: null,
                    endDate: null,
                    description: null,
                    recurring: false,
                    applicableAtCourtRegistryIds: [],
                    applicableAtInterviewOrganisationIds: [],
                },

                courtRegistryIds: [],
                interviewOrganisationIds: [],
            };
        },
        watch: {
            courtRegistryIds: function () {
                this.refreshEvents();
            },
            interviewOrganisationIds: function () {
                this.refreshEvents();
            },
        },
        methods: { 
            getData: function (args) {
                console.log(JSON.parse(args.data));
                var params = JSON.parse(args.data);

                var data = {
                    startDate: this.parseDateTime(params.StartDate, null, true, false),
                    endDate: this.parseDateTime(params.EndDate, null, true, false),
                    courtRegistryIds: this.courtRegistryIds,
                    interviewOrganisationIds: this.interviewOrganisationIds,
                };

                var url = this.$urlHelper.getApiUrl('PublicHoliday', 'schedule');
                axios
                    .post(url, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(response => {
                        args.onSuccess(response.data);
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                    });
            },

            getEventDateString(event) {
                var startDate = this.formatDateTime(event.startTime, 'DD/MM/YYYY');
                var endDate = this.formatDateTime(event.endTime, 'DD/MM/YYYY');
                if (endDate == startDate) return startDate;
                return startDate + " - " + endDate;
            },

            clearFilters: function () {
                this.courtRegistryIds = [];
                this.interviewOrganisationIds = [];
                this.refreshEvents();
            },

            confirmDeleteEvent: function () {
                this.$bvModal.msgBoxConfirm(
                    [
                        this.$createElement(
                            'div',
                            {
                                domProps:
                                {
                                    innerHTML: "<p>Are you sure you want to DELETE the event '" + this.currentEvent.description + "'?</p>"
                                }
                            }
                        )
                    ],
                    {
                        title: 'Delete Event',
                        size: 'md',
                        okVariant: 'danger',
                        okTitle: 'Yes, DELETE Event',
                        cancelTitle: 'No, Do Not Delete',
                        centered: true
                    })
                    .then(value => {
                        if (value) {
                            this.deleteEvent();
                            this.refreshGlossary();
                        }
                    })
            },
            deleteEvent: function () {
                this.currentErrors = [];

                var data = {
                    publicHolidayId: this.currentEvent.publicHolidayId,
                };

                console.log(data);

                var url = this.$urlHelper.getApiUrl('publicholiday') + '/' + data.publicHolidayId;
                console.log(url);

                axios
                    .delete(url)
                    .then(() => {
                        this.createEventDialogShown = false;
                        this.refreshEvents();  
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length != 0) {
                            this.showErrorsToast(this.currentErrors);
                        }
                    });
            },
            onPopupOpen: function (args) {
                //console.log('onPopupOpen: ' + JSON.stringify(args.data));
                //console.log('count: ' + args.data.event.length());
                args.cancel = true;

                if (args.data.event) {
                    return;
                }

                this.currentEvent.startDate = args.data.startTime;

                if (args.data.id) {
                    if (!this.canModifyPublicHolidays) { return; }
                    this.createOrEditTitle = 'Edit Public Holiday \'' + args.data.subject + '\'';
                    this.currentEvent.publicHolidayId = args.data.id;
                    this.currentEvent.description = args.data.subject;
                    this.currentEvent.endDate = args.data.endTime;
                    this.currentEvent.recurring = args.data.recurring;
                    this.currentEvent.applicableAtCourtRegistryIds = args.data.applicableAtCourtRegistryIds;
                    this.currentEvent.applicableAtInterviewOrganisationIds = args.data.applicableAtInterviewOrganisationIds;
                }
                else {
                    if (!this.canCreatePublicHolidays) { return; }
                    this.createOrEditTitle = 'Create Public Holiday';
                    this.currentEvent.publicHolidayId = null;
                    this.currentEvent.description = '';
                    this.currentEvent.endDate = null;
                    this.currentEvent.recurring = args.data.recurring;
                    this.currentEvent.applicableAtCourtRegistryIds = null;
                    this.currentEvent.applicableAtInterviewOrganisationIds = null;
                }

                this.createEventDialogShown = true;
            },
            createOrEditEvent: function () {
                var data = {
                    publicHolidayId: this.currentEvent.publicHolidayId,
                    description: this.currentEvent.description,
                    startDateTime: this.formatDateTime(this.currentEvent.startDate, "yyyy-MM-DD", false, false).toString(),
                    recurring: this.currentEvent.recurring,
                    applicableAtCourtRegistryIds: this.currentEvent.applicableAtCourtRegistryIds,
                    applicableAtInterviewOrganisationIds: this.currentEvent.applicableAtInterviewOrganisationIds,
                };

                if (this.currentEvent.endDate) {
                    if (this.currentEvent.endDate < this.currentEvent.startDate) {
                        var message = 'End Date cannot be before Start Date.';
                        this.showErrorsToast([message]);
                        return;
                    }

                    data.endDateTime = this.formatDateTime(this.currentEvent.endDate, "yyyy-MM-DD", false, false).toString()
                }

                var url = this.$urlHelper.getApiUrl('publicholiday');

                axios
                    .post(url, JSON.stringify(data),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.createEventDialogShown = false;   
                        this.refreshEvents();
                    })
                    .catch(ex => {
                        var errors = this.handleAxiosException(ex);
                        this.showErrorsToast(errors);
                    });
            },
            resetDateValidationProviders: function () {
                this.$refs.startDateValidationProvider.reset();
                this.$refs.endDateValidationProvider.reset();
            },
            refreshEvents: function () {
                this.schedulerInstance.refresh();
            },
            onEventRendered: function (args) {                
                args.element.style.backgroundColor = args.data.color;
            },
        },
        mounted: function () {
            this.schedulerInstance = this.$refs.schedule.ej2Instances;
        }
    }
</script>

<style>
    .e-schedule-event-tooltip.tooltip-wrap .content-area {
        padding-left: 6px;
        font-size: 12px;
        margin: 0px;
    }

    .e-schedule-event-tooltip.tooltip-wrap .eventname {
        font-weight: 500;
        font-size: 14px;
    }
</style>